import axios from '@axios'
import {
  localListAddItem,
  localListDeleteItem,
  localListEnrichLocalId,
  localListFindIndex,
  localListUpdateItem,
} from '@/store/utils'

/**
 * A identidade de uma linha é (UF, tipo de operação, UF de destino) — a mesma key única da
 * tabela. Usada para evitar duplicata antes mesmo de ir ao servidor.
 */
const isSameItemKey = (a, b) =>
  (a.state || null) === (b.state || null) &&
  (a.operationType || 'INTERNAL') === (b.operationType || 'INTERNAL') &&
  (a.destinationState || '') === (b.destinationState || '')

/**
 * Tributos da reforma. Ficam no cabeçalho porque suas alíquotas são únicas para todos os estados
 * e municípios: não há o que variar por UF, ao contrário das linhas.
 */
const getInitialTaxReform = () => ({
  stCbsIbsCode: null,
  fiscalClassificationCbsIbsCode: null,
  cbsPercent: null,
  cbsReductionPercent: null,
  cbsDeferralPercent: null,
  ibsStatePercent: null,
  ibsStateReductionPercent: null,
  ibsStateDeferralPercent: null,
  ibsMunicipalPercent: null,
  ibsMunicipalReductionPercent: null,
  ibsMunicipalDeferralPercent: null,
  stSelectiveTaxCode: null,
  fiscalClassificationSelectiveTaxCode: null,
  selectiveTaxType: null,
  selectiveTaxPercent: null,
  selectiveTaxValuePerUnit: null,
})

const getInitialState = () => ({
  taxConfigurationGroup: {
    id: 0,
    name: '',
    description: '',
    ncm: null,
    active: true,
    skuCount: 0,
    ...getInitialTaxReform(),
  },
  items: [],
  /**
   * Controle de tela, não campo do cadastro. Desligado, o bloco inteiro vai nulo para a API e o
   * produto continua usando o parâmetro fiscal do NCM. É derivado na edição: o que grava o
   * estado é a presença das situações tributárias.
   */
  taxReformEnabled: false,
})

export default {
  namespaced: true,
  state: getInitialState(),

  getters: {
    /** Linhas visíveis na grid: as marcadas para exclusão saem da tela na hora. */
    visibleItems(state) {
      return state.items.filter(item => !item.isDeleted)
    },

    itemsForSave(state) {
      return {
        new: state.items.filter(item => !item.id && !item.isDeleted),
        updated: state.items.filter(item => item.id && !item.isDeleted),
        // Só as que existem no servidor precisam ser excluídas de fato.
        deleted: state.items.filter(item => item.id && item.isDeleted),
      }
    },

    /**
     * Já existe outra linha com a mesma key? A grid mostra uma linha por key, então a duplicata
     * esconderia uma das duas; o back também a rejeita.
     *
     * A linha em edição não conflita com ela própria: a posição dela vem do mesmo
     * `localListFindIndex` que as demais operações da lista usam — `id` no registro que veio do
     * servidor, `localId` no que foi adicionado na tela. Um item ainda não adicionado não está
     * na lista e o índice volta -1, que nenhuma posição iguala.
     */
    hasItemWithSameKey(state) {
      return item => {
        const ownIndex = localListFindIndex(state.items, item)

        return state.items.some((current, index) => index !== ownIndex
          && !current.isDeleted
          && isSameItemKey(current, item))
      }
    },

    /** Um cadastro em uso não pode ser inativado: os produtos voltariam para a fiscal do NCM. */
    isInUse(state) {
      return (state.taxConfigurationGroup.skuCount || 0) > 0
    },

    /**
     * Tributos da reforma no formato do contrato da API. Cada bloco é tudo ou nada: sem situação
     * tributária o bloco inteiro vai nulo, e o parâmetro fiscal do NCM continua valendo.
     */
    taxReformForSave(state) {
      const group = state.taxConfigurationGroup
      const empty = getInitialTaxReform()

      if (!state.taxReformEnabled) return empty

      const cbsIbs = group.stCbsIbsCode
        ? {
          stCbsIbsCode: group.stCbsIbsCode,
          fiscalClassificationCbsIbsCode: group.fiscalClassificationCbsIbsCode,
          cbsPercent: group.cbsPercent,
          cbsReductionPercent: group.cbsReductionPercent,
          cbsDeferralPercent: group.cbsDeferralPercent,
          ibsStatePercent: group.ibsStatePercent,
          ibsStateReductionPercent: group.ibsStateReductionPercent,
          ibsStateDeferralPercent: group.ibsStateDeferralPercent,
          ibsMunicipalPercent: group.ibsMunicipalPercent,
          ibsMunicipalReductionPercent: group.ibsMunicipalReductionPercent,
          ibsMunicipalDeferralPercent: group.ibsMunicipalDeferralPercent,
        }
        : {}

      const selectiveTax = group.stSelectiveTaxCode
        ? {
          stSelectiveTaxCode: group.stSelectiveTaxCode,
          fiscalClassificationSelectiveTaxCode: group.fiscalClassificationSelectiveTaxCode,
          selectiveTaxType: group.selectiveTaxType,
          selectiveTaxPercent: group.selectiveTaxPercent,
          selectiveTaxValuePerUnit: group.selectiveTaxValuePerUnit,
        }
        : {}

      return { ...empty, ...cbsIbs, ...selectiveTax }
    },
  },

  mutations: {
    SET_TAX_CONFIGURATION_GROUP(state, taxConfigurationGroup) {
      state.taxConfigurationGroup = taxConfigurationGroup
    },
    SET_ITEMS(state, items) {
      state.items = items
    },
    SET_TAX_REFORM_ENABLED(state, enabled) {
      state.taxReformEnabled = enabled
    },
    SET_TAX_REFORM(state, taxReform) {
      state.taxConfigurationGroup = {
        ...state.taxConfigurationGroup,
        ...getInitialTaxReform(),
        ...taxReform,
      }
    },
    ADD_ITEM(state, item) {
      state.items = localListAddItem(state.items, item)
    },
    UPDATE_ITEM(state, item) {
      state.items = localListUpdateItem(state.items, item)
    },
    REMOVE_ITEM(state, idOrLocalId) {
      state.items = localListDeleteItem(state.items, idOrLocalId)
    },
    CLEAN_STATE(state) {
      const { taxConfigurationGroup, items, taxReformEnabled } = getInitialState()
      state.taxConfigurationGroup = taxConfigurationGroup
      state.items = items
      state.taxReformEnabled = taxReformEnabled
    },
  },

  actions: {
    async fetchTaxConfigurationGroupById({ commit }, id) {
      const { data } = await axios.get(`/api/tax/tax-configuration-groups/${id}`)

      commit('SET_TAX_CONFIGURATION_GROUP', {
        id: data.id,
        name: data.name,
        description: data.description,
        ncm: data.ncm,
        active: data.active,
        skuCount: data.skuCount,
        stCbsIbsCode: data.stCbsIbsCode,
        fiscalClassificationCbsIbsCode: data.fiscalClassificationCbsIbsCode,
        cbsPercent: data.cbsPercent,
        cbsReductionPercent: data.cbsReductionPercent,
        cbsDeferralPercent: data.cbsDeferralPercent,
        ibsStatePercent: data.ibsStatePercent,
        ibsStateReductionPercent: data.ibsStateReductionPercent,
        ibsStateDeferralPercent: data.ibsStateDeferralPercent,
        ibsMunicipalPercent: data.ibsMunicipalPercent,
        ibsMunicipalReductionPercent: data.ibsMunicipalReductionPercent,
        ibsMunicipalDeferralPercent: data.ibsMunicipalDeferralPercent,
        stSelectiveTaxCode: data.stSelectiveTaxCode,
        fiscalClassificationSelectiveTaxCode: data.fiscalClassificationSelectiveTaxCode,
        selectiveTaxType: data.selectiveTaxType,
        selectiveTaxPercent: data.selectiveTaxPercent,
        selectiveTaxValuePerUnit: data.selectiveTaxValuePerUnit,
      })
      commit('SET_ITEMS', localListEnrichLocalId(data.items))
      commit('SET_TAX_REFORM_ENABLED', !!(data.stCbsIbsCode || data.stSelectiveTaxCode))
    },

    // A key repetida é barrada na tela, com mensagem: aqui a guarda evita que uma chamada sem
    // essa checagem esconda uma linha na grid.
    addItem({ commit, getters }, item) {
      if (getters.hasItemWithSameKey(item)) return false

      commit('ADD_ITEM', item)
      return true
    },

    updateItem({ commit, getters, state }, item) {
      if (localListFindIndex(state.items, item) < 0) return false
      if (getters.hasItemWithSameKey(item)) return false

      commit('UPDATE_ITEM', item)
      return true
    },

    deleteItem({ commit }, item) {
      commit('REMOVE_ITEM', item.id || item.localId)
    },

    setTaxConfigurationGroup({ commit }, taxConfigurationGroup) {
      commit('SET_TAX_CONFIGURATION_GROUP', taxConfigurationGroup)
    },

    setTaxReformEnabled({ commit }, enabled) {
      commit('SET_TAX_REFORM_ENABLED', enabled)
    },

    /** Substitui o bloco inteiro: o que o pré-preenchimento não trouxer volta a ficar nulo. */
    setTaxReform({ commit }, taxReform) {
      commit('SET_TAX_REFORM', taxReform)
    },

    cleanState({ commit }) {
      commit('CLEAN_STATE')
    },
  },
}
